/**
 * Аватар и группа аватаров.
 *
 * Инициалы берутся по-русски: «Иванов Алексей» → «ИА». Цвет фона выводится
 * из имени детерминированно — один и тот же человек всегда одного цвета,
 * и это работает без запроса к серверу.
 */

import * as React from "react";
import * as RadixAvatar from "@radix-ui/react-avatar";

import { cn } from "@/shared/lib/cn";

const SIZES = {
  sm: "size-[20px] text-[10px]",
  md: "size-[24px] text-[11px]",
  lg: "size-[32px] text-[13px]",
} as const;

/** Восемь цветов визуализации из палитры: девятого в токенах нет. */
const TONES = 8;

export interface AvatarProps {
  name: string;
  src?: string | undefined;
  size?: keyof typeof SIZES;
  className?: string;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const second = parts[1]?.[0] ?? "";
  return (first + second).toUpperCase() || "?";
}

function toneOf(name: string): number {
  let hash = 0;
  for (const char of name) {
    hash = (hash * 31 + (char.codePointAt(0) ?? 0)) % 1_000_003;
  }
  return (hash % TONES) + 1;
}

export function Avatar({ name, src, size = "md", className }: AvatarProps): React.ReactElement {
  const tone = toneOf(name);
  return (
    <RadixAvatar.Root
      className={cn(
        "inline-flex shrink-0 select-none items-center justify-center overflow-hidden",
        "rounded-[var(--pt-radius-full)] font-medium",
        SIZES[size],
        className,
      )}
      title={name}
    >
      <RadixAvatar.Image src={src} alt={name} className="size-full object-cover" />
      <RadixAvatar.Fallback
        className="flex size-full items-center justify-center text-[var(--pt-text-on-accent)]"
        style={{ backgroundColor: `var(--pt-viz-${tone})` }}
        delayMs={src ? 300 : 0}
      >
        {initials(name)}
      </RadixAvatar.Fallback>
    </RadixAvatar.Root>
  );
}

export interface AvatarGroupProps {
  people: readonly { name: string; src?: string }[];
  /** Сколько показать до сворачивания в «+N». */
  max?: number;
  size?: keyof typeof SIZES;
  className?: string;
}

export function AvatarGroup({
  people,
  max = 3,
  size = "md",
  className,
}: AvatarGroupProps): React.ReactElement {
  const shown = people.slice(0, max);
  const hidden = people.slice(max);

  return (
    <div className={cn("flex items-center", className)}>
      {shown.map((person) => (
        <Avatar
          key={person.name}
          name={person.name}
          src={person.src}
          size={size}
          className="-ml-[6px] ring-2 ring-[var(--pt-bg-surface)] first:ml-0"
        />
      ))}
      {hidden.length > 0 && (
        <span
          className={cn(
            "-ml-[6px] inline-flex items-center justify-center rounded-[var(--pt-radius-full)]",
            "bg-[var(--pt-bg-sunken)] text-[var(--pt-text-secondary)] font-medium",
            "ring-2 ring-[var(--pt-bg-surface)]",
            SIZES[size],
          )}
          title={hidden.map((person) => person.name).join(", ")}
        >
          +{hidden.length}
        </span>
      )}
    </div>
  );
}
